import React, { useEffect, useState } from 'react';
import { Box, Grid, Stack } from '@mui/material';
import PropTypes from 'prop-types';
import { useMutation } from 'react-query';
import axios from 'api/request';
import { endPoints } from 'api/endpoints';
import Select, { CssTextField } from 'pages/reference-implementation/components/select';
import { InputLabel } from 'pages/reference-implementation/components/input-label';
import { RIStyles } from 'pages/reference-implementation/Style';
import Btn from 'components/button';
import { commonStyles as style } from 'assets/style';
import DbNameHelpImg, { getDbNameHelperText } from './dbNameHelpers';
import SqlServiceAccountName from './components/SqlServiceAccountName';

const SqlDatabase = (props) => {
    const { resource, handleDrawerAction, order } = props;

    const [formData, setFormData] = useState({
        sqlDatabaseVersion: resource?.configureData?.sqlDatabaseVersion || 'SQL Server 2022',
        sqlDatabaseEdition: resource?.configureData?.sqlDatabaseEdition || 'enterprise',
        databaseName: resource?.configureData?.databaseName || '',
        domain: resource?.configureData?.domain || null,
        sqlAccountName: resource?.configureData?.sqlAccountName || '',
    });

    const [domainOptions, setDomainOptions] = useState([]);
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [isValid, setIsValid] = useState(false);
    const [isNumber, setIsNumber] = useState(false);
    const [isSqlAccountNameValid, setIsSqlAccountNameValid] = useState(false);

    const domainApi = useMutation(() => axios.get(`${endPoints.ssp.vm.adDomain}/windows`));

    useEffect(() => {
        domainApi.mutate();
    }, []);

    useEffect(() => {
        if (domainApi?.isSuccess && domainApi?.data?.data?.domains) {
            const options = domainApi.data.data.domains.map((d) => ({ label: d.name, value: d.name }));
            setDomainOptions(options);
            if (options.length === 1 && !formData.domain) {
                setFormData((prev) => ({ ...prev, domain: options[0].value }));
            }
        }
    }, [domainApi?.isSuccess]);

    const validateForm = () => {
        const newErrors = {};

        if (!formData.databaseName) {
            newErrors.databaseName = { type: 'required', message: '' };
        } else if (/^[0-9]*$/.test(formData.databaseName)) {
            newErrors.databaseName = { type: 'pattern', message: 'Database name should not start with numerics' };
        } else if (/\s/.test(formData.databaseName)) {
            newErrors.databaseName = { type: 'pattern', message: 'Database Name must not contain spaces' };
        } else if (!/^(?![0-9]*$)([A-Z0-9]){2,8}$/.test(formData.databaseName)) {
            newErrors.databaseName = {
                type: 'pattern',
                message: 'Database name should be alphanumeric of 2-8 chars length and special characters are not allowed',
            };
        }
        if (!formData.domain) {
            newErrors.domain = { type: 'required', message: 'Domain is required' };
        }
        if (!formData.sqlAccountName) {
            newErrors.sqlAccountName = { type: 'required', message: 'Active Directory SQL Account is required' };
        } else if (!isSqlAccountNameValid) {
            newErrors.sqlAccountName = { type: 'validate', message: 'Given Service Account does not exist.' };
        }

        setErrors(newErrors);
        return { isValid: Object.keys(newErrors).length === 0, errors: newErrors };
    };

    const handleOnSelectChange = (key, value) => {
        setFormData({
            ...formData,
            [key]: value,
        });
        setTouched({
            ...touched,
            [key]: true,
        });
    };

    const checkDBValidation = (val) => {
        if (val && val !== '') {
            if (/^[0-9]*$/.test(val)) {
                setIsNumber(true);
                setIsValid(false);
            } else if (/^(?![0-9]*$)([A-Z0-9]){2,8}$/.test(val)) {
                setIsValid(false);
                setIsNumber(false);
            } else if (/^[a-z]*$/.test(val)) {
                setIsNumber(false);
                setIsValid(false);
            } else {
                setIsNumber(false);
                setIsValid(true);
            }
        } else {
            setIsNumber(false);
            setIsValid(false);
        }
    };

    const handleSubmit = () => {
        setTouched({
            databaseName: true,
            domain: true,
            sqlAccountName: true,
        });
        const checkFormValid = validateForm();
        handleDrawerAction('save', formData, checkFormValid);
    };

    const shouldShowError = (field) => {
        return touched[field] && !!errors[field];
    };

    useEffect(() => {
        if (Object.keys(touched).length > 0) {
            validateForm();
        }
    }, [formData]);

    const saveButton = () => (
        <Btn className="custom-btn" variant="contained" sx={style.modalSaveButton} onClick={() => handleSubmit()} data-testid="save-btn">
            Save
        </Btn>
    );

    const cancelButton = () => (
        <Btn className="custom-btn" variant="outlined" sx={style.modalCancelButton} onClick={() => handleDrawerAction('cancel')} data-testid="cancel-btn">
            Cancel
        </Btn>
    );

    return (
        <Box style={RIStyles.tags.wrapper} className="ri-layout">
            <Box sx={RIStyles.tags.contentWrapper}>
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <Box display="flex" alignItems="center" gap={1}>
                            <Select
                                key="sqlDatabaseVersion"
                                label="SQL Database Version"
                                value={formData.sqlDatabaseVersion}
                                options={[{ label: 'SQL Server 2022', value: 'SQL Server 2022' }]}
                                disabled
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={6}>
                        <Box display="flex" alignItems="center" gap={1}>
                            <Select
                                key="sqlDatabaseEdition"
                                label="SQL Database Edition"
                                value={formData.sqlDatabaseEdition}
                                options={[{ label: 'Enterprise', value: 'enterprise' }]}
                                disabled
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={6}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <CssTextField
                                size="small"
                                sx={{ width: (theme) => theme.spacing('100%') }}
                                autoComplete="off"
                                label={<InputLabel label="Database Name" isRequired />}
                                placeholder="TPMFLN1P"
                                error={isNumber || isValid || shouldShowError('databaseName')}
                                helperText={getDbNameHelperText(isNumber, isValid, touched, errors)}
                                value={formData.databaseName}
                                onChange={(e) => {
                                    const value = e.target.value.toLocaleUpperCase();
                                    checkDBValidation(value);
                                    handleOnSelectChange('databaseName', value);
                                }}
                                className={isNumber || isValid || shouldShowError('databaseName') ? 'validate' : ''}
                            />
                            <DbNameHelpImg />
                        </Stack>
                    </Grid>
                    <Grid item xs={6}>
                        <Box display="flex" alignItems="center" gap={1}>
                            <Select
                                key="domain"
                                label="Domain *"
                                value={formData?.domain || null}
                                options={domainOptions}
                                handleOnSelect={(value) => handleOnSelectChange('domain', value?.value)}
                                isRequired={shouldShowError('domain')}
                                isLoading={domainApi?.isLoading}
                                disabled={domainApi?.isLoading}
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={6}>
                        <SqlServiceAccountName
                            value={formData.sqlAccountName}
                            environment={order?.environment}
                            isRequired={shouldShowError('sqlAccountName') || touched?.sqlAccountName}
                            onChange={(sqlAccountName) => handleOnSelectChange('sqlAccountName', sqlAccountName)}
                            onValidationChange={(valid) => setIsSqlAccountNameValid(valid)}
                        />
                    </Grid>
                </Grid>
            </Box>
            <Box className="footer" sx={RIStyles.tags.actionWrapper} gap={2}>
                {saveButton()}
                {cancelButton()}
            </Box>
        </Box>
    );
};

SqlDatabase.propTypes = {
    resource: PropTypes.any.isRequired,
    handleDrawerAction: PropTypes.func.isRequired,
    order: PropTypes.object.isRequired,
};

export default SqlDatabase;
